import { LogGroup, now, Log, NextTask, NextTaskAsync } from ".././MyHelpers";
/** possible change "edges" */
export enum StateChange
{
	Begin,
	Running,
	End
}

/** callback for specific state change */
export type StateMachineCallback = () => void;

/** callback to get state name (currently can't convert export enum to string representation when using generics?) */
export type GetStateNameCallback<T> = (state: T) => string;

/** generic state change callback, called for each state change */
export type StateMachineStateChangeCallback<T> = (oldState: T, newState: T, stateNameCallback: GetStateNameCallback<T>) => void;

export interface ChangeStateParams
{
	quiet: boolean;
	reason: string;
}

export enum ChangeStateType
{
	Immediate = "immediate",
	NextTask = "nexttask",
}

export class StateMachine<T>
{
	protected state: T;
	protected stateChangeCallback: StateMachineStateChangeCallback<T> = null;
	protected stateRunningCallback: StateMachineCallback = null;

	public EndCallback: (endState: T) => void = null;
	public BeginCallback: (beginState: T) => void = null;

	protected stateBeginCallbacks: Map<T, StateMachineCallback> = new Map<T, StateMachineCallback>();
	protected stateRunningCallbacks: Map<T, StateMachineCallback> = new Map<T, StateMachineCallback>();
	protected stateEndCallbacks: Map<T, StateMachineCallback> = new Map<T, StateMachineCallback>();

	//protected logGroup: string = "StateMachine";
	protected logGroup: LogGroup = new LogGroup("StateMachine");

	protected getStateNameCallback = null;

	protected lastStateChange: number = now();

	protected readonly MAX_SAME_STATE = 5;
	protected sameStateCount: number = 0

	public constructor(defaultState: T, logGroup: LogGroup = null, getStateNameCallback: GetStateNameCallback<T> = null) {
		this.state = defaultState;
		this.logGroup = logGroup.Add("StateMachine");

		this.getStateNameCallback = getStateNameCallback;

		if (this.getStateNameCallback == null) {
			this.getStateNameCallback = (state: T) => { return "" + state; };
		}
	}

	public ChangeState(newState: T, params: Partial<ChangeStateParams> = null, changeType: ChangeStateType = ChangeStateType.Immediate) 
	{
		// Log.Debug(this.logGroup, "Changing state " + this.getStateNameCallback(this.state) + " -> " + this.getStateNameCallback(newState));

		// call generic callback, when any state change occurs
		// call it first, so we don't have to remember "old" state, because current state has not yet been changed
		// current this.state represents "old" state, and parameter "newState" will be saved to this.state

		if (newState == this.state)
		{
			this.sameStateCount++;

			if (this.sameStateCount == this.MAX_SAME_STATE)
			{
				// throw "State machine loop detected!";
				console.warn("State machine loop detected!");
			}
		}
		else
		{
			this.sameStateCount = 0;
		}

		let oldState = this.state;
		this.state = newState;

		if (this.stateChangeCallback != null && (!params || !params.quiet)) {
			this.stateChangeCallback(oldState, newState, this.getStateNameCallback);
		}

		let endCallback = this.stateEndCallbacks.get(oldState);
		if (endCallback && (!params || !params.quiet)) 
		{
			//setTimeout(() => endCallback(), 0);
			this.EndCallback?.(oldState);
			endCallback();
		}

		this.lastStateChange = now();

		let beginCallback = this.stateBeginCallbacks.get(this.state);
		if (beginCallback && (!params || !params.quiet)) 
		{
			//setTimeout(() => beginCallback(), 0);
			if (changeType == ChangeStateType.NextTask)
			{
				NextTaskAsync(() => 
				{
					this.BeginCallback?.(newState);
					beginCallback();
				})
				.then(() => {})
				.catch(() => {})
			}
			else
			{
				this.BeginCallback?.(newState);
				beginCallback();
			}
		}

		this.stateRunningCallback = this.stateRunningCallbacks.get(this.state);
	}

	public SetCallback(state: T, stateChange: StateChange, callback: StateMachineCallback): void {
		if (stateChange == StateChange.Begin) {
			this.stateBeginCallbacks.set(state, callback);
		}
		else if (stateChange == StateChange.End) {
			this.stateEndCallbacks.set(state, callback);
		}
		else if (stateChange == StateChange.Running) {
			this.stateRunningCallbacks.set(state, callback);
		}
	}

	public SetStateChangeCallback(callback: StateMachineStateChangeCallback<T>): void {
		this.stateChangeCallback = callback;
	}

	public State(): T {
		return this.state;
	}

	public get StateName(): string
	{
		return this.getStateNameCallback(this.state);
	}

	public IsState(requiredState: T): boolean
	{
		return this.state == requiredState;
	}

	public AssertState(requiredState: T): void
	{
		if (!this.IsState(requiredState))
		{
			let stateName = (s: T) => "[" + s + "] " + this.getStateNameCallback(s);

			let requiredStateName: string = stateName(requiredState);
			let currentStateName: string =  stateName(this.state);
			throw `Required state is ${requiredStateName}, current state is ${currentStateName}`;
		}
	}

	public Update(): void {
		if (this.stateRunningCallback) {
			this.stateRunningCallback();
		}
	}

	public LastStateChange(): number {
		return this.lastStateChange;
	}

	public TimeSinceLastStateChange(): number {
		return now() - this.lastStateChange;
	}
}